import { api } from '../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../shared/api/genexus-query';
import type { CrudApi } from '../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../shared/components/DataTable';
import type {
  TimesheetEntryDto,
  TimesheetEntryInput,
  TimesheetEntryListItem,
  TimesheetEntryTotalsFilters,
  TimesheetEntryTotalsResponse,
} from './types';

const BASE = '/v1/timesheet-entries';

export const timesheetEntriesApi: CrudApi<
  TimesheetEntryListItem,
  TimesheetEntryDto,
  TimesheetEntryInput,
  string
> = {
  async list(query: DataTableQuery): Promise<ListEnvelope<TimesheetEntryListItem>> {
    return listWithFilters(query, {});
  },

  async get(id: string): Promise<TimesheetEntryDto> {
    const { data } = await api.get<{ data: TimesheetEntryDto }>(`${BASE}/${id}`);
    return data.data;
  },

  async create(input: TimesheetEntryInput): Promise<TimesheetEntryDto> {
    const { data } = await api.post<{ data: TimesheetEntryDto }>(BASE, input);
    return data.data;
  },

  async update(id: string, input: TimesheetEntryInput): Promise<TimesheetEntryDto> {
    // FKs que compõem a PK (colaborador/cliente/projeto) são ignoradas no server (spec § 6).
    const { data } = await api.put<{ data: TimesheetEntryDto }>(`${BASE}/${id}`, input);
    return data.data;
  },

  async remove(id: string): Promise<void> {
    // DELETE físico — não há soft-delete neste recurso.
    await api.delete(`${BASE}/${id}`);
  },
};

// Wire: List e /Totals do KB usam o param `employeeId` (2026-07-04, card lRdvNxWj).
// Internamente o front carrega `colaboradorCodigo` como campo de domínio (PT-BR); a
// tradução acontece só aqui na fronteira antes de sair pro upstream.
function toWireFilters(
  filters: TimesheetEntryTotalsFilters,
): Record<string, string | number | boolean | null | undefined> {
  const { colaboradorCodigo, ...rest } = filters;
  return colaboradorCodigo == null ? rest : { ...rest, employeeId: colaboradorCodigo };
}

export async function listWithFilters(
  query: DataTableQuery,
  filters: TimesheetEntryTotalsFilters,
): Promise<ListEnvelope<TimesheetEntryListItem>> {
  const { data } = await api.get<{
    data: TimesheetEntryListItem[];
    meta: { total: number; pagina?: number; porPagina?: number };
  }>(BASE, { params: toGenexusParams(query, toWireFilters(filters)) });
  return normalizeListEnvelope(data);
}

/**
 * Export xlsx do relatório de apontamentos — `ApiTimesheetEntryRelatorio`
 * (card [kqYokafH], espelha o relatório-extrato da NF Empresa/ATD-0047). O BFF
 * decodifica o base64 e faz stream do `.xlsx`. Filtros passam por `toWireFilters`
 * (`colaboradorCodigo` → `employeeId`, igual à List/Totals).
 */
export async function downloadApontamentosRelatorio(
  filters: TimesheetEntryTotalsFilters,
): Promise<Blob> {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(toWireFilters(filters))) {
    if (v === undefined || v === null || v === '') continue;
    params[k] = String(v);
  }
  const res = await api.get<Blob>(`${BASE}/relatorio`, { params, responseType: 'blob' });
  return res.data;
}

export async function getTotals(
  filters: TimesheetEntryTotalsFilters,
): Promise<TimesheetEntryTotalsResponse> {
  // DIVERGÊNCIA DE PARAM: o `/Totals` filtra por `colaboradorCodigo` — ao
  // contrário da List/relatório, que usam `employeeId` — e IGNORA `employeeId`.
  // Verificado no DES: `/Totals?employeeId=5` = total sem filtro; só
  // `?colaboradorCodigo=5` filtra. Por isso NÃO passamos por `toWireFilters`.
  // Ver memória `timesheet-list-param-divergencia`.
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    params[k] = String(v);
  }
  const { data } = await api.get<{ data: TimesheetEntryTotalsResponse }>(`${BASE}/totals`, {
    params,
  });
  return data.data;
}
